import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Users, UserPlus, Flame, CalendarClock, GraduationCap, RefreshCw } from "lucide-react";
import { getStats } from "../../lib/api";

const CARDS = [
  { key: "total", label: "Total Leads", icon: Users, color: "text-white" },
  { key: "new_today", label: "New Today", icon: UserPlus, color: "text-[#22C55E]" },
  { key: "hot", label: "Hot Leads", icon: Flame, color: "text-[#EF4444]" },
  { key: "due_today", label: "Follow-ups Due Today", icon: CalendarClock, color: "text-[#FACC15]" },
  { key: "enrolled_month", label: "Enrolled This Month", icon: GraduationCap, color: "text-[#22C55E]" },
];

function TopList({ title, items, testid }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] p-5" data-testid={testid}>
      <h3 className="font-heading uppercase text-white text-xl tracking-wide mb-4">{title}</h3>
      {items.length === 0 && <p className="text-sm text-[#6B7A70]">No data yet.</p>}
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[#A7B3AA] truncate pr-3">{i.label}</span>
              <span className="text-[#FACC15] font-bold shrink-0">{i.count}</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#17231B] overflow-hidden">
              <div className="h-full bg-[#22C55E] rounded-full" style={{ width: `${(i.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setStats(await getStats());
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dashboard-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.1] p-5 h-24 animate-pulse" />
        ))}
      </div>
    );
  }
  if (error) return <p className="text-sm text-[#EF4444]" data-testid="dashboard-error">{error}</p>;
  if (!stats) return null;

  return (
    <div data-testid="dashboard-view">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-wide text-white">Dashboard</h1>
        <button onClick={load} data-testid="dashboard-refresh" className="inline-flex items-center gap-2 rounded-full border border-[#22C55E]/30 text-[#A7B3AA] hover:text-white px-4 py-2 text-sm transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dashboard-cards">
        {CARDS.map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] p-4 sm:p-5" data-testid={`stat-card-${key}`}>
            <div className="flex items-center justify-between">
              <p className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-[#6B7A70]">{label}</p>
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <p className={`mt-2 font-heading text-3xl sm:text-4xl ${color}`}>{stats.cards[key]}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] p-5 sm:p-6" data-testid="dashboard-funnel">
        <h3 className="font-heading uppercase text-white text-xl tracking-wide mb-5">Sales Funnel</h3>
        <div className="space-y-4">
          {stats.funnel.map((f, i) => {
            const max = Math.max(1, stats.funnel[0]?.count || 1);
            return (
              <div key={f.stage} className="flex items-center gap-3 sm:gap-5">
                <span className="w-28 sm:w-36 shrink-0 text-xs sm:text-sm text-[#A7B3AA]">{f.stage}</span>
                <div className="flex-1 h-8 rounded-lg bg-[#17231B] overflow-hidden">
                  <div
                    className={`h-full rounded-lg flex items-center px-3 transition-all duration-700 ${i === stats.funnel.length - 1 ? "bg-[#22C55E]" : "bg-[#22C55E]/60"}`}
                    style={{ width: `${Math.max(4, (f.count / max) * 100)}%` }}
                  >
                    <span className="text-xs font-extrabold text-[#04100A]">{f.count}</span>
                  </div>
                </div>
                <span className="w-14 shrink-0 text-right text-xs sm:text-sm font-bold text-[#FACC15]">{f.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] p-5 sm:p-6 lg:col-span-2" data-testid="dashboard-chart">
          <h3 className="font-heading uppercase text-white text-xl tracking-wide mb-4">Leads Per Day — Last 30 Days</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.per_day} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fill: "#6B7A70", fontSize: 10 }} interval={4} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(34,197,94,0.08)" }}
                  contentStyle={{ background: "#0F1712", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 12, fontSize: 12 }}
                  labelStyle={{ color: "#A7B3AA" }}
                  itemStyle={{ color: "#22C55E" }}
                />
                <Bar dataKey="count" fill="#22C55E" radius={[4, 4, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <TopList title="Top Biggest Problems" items={stats.top_problems} testid="dashboard-top-problems" />
        <TopList title="Top Sources / Campaigns" items={stats.top_sources} testid="dashboard-top-sources" />
      </div>
    </div>
  );
}
